import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from './index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import * as XLSX from 'xlsx';
import { addNewBulk } from "../../../redux/features/bulkUpload/bulkUploadService";
import { getAllActiveCourses } from "../../../redux/features/course/courseService";
import Dropdown from "../../../components/Dropdown/Dropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";

const BulkUpload = () => {

    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const courseDropdownRef = useRef(null);

    const { isBulkCreating } = useSelector((state) => state.bulk.newBulk);
    const { activeCourses } = useSelector((state) => state.course.allActiveCourses);


    const [formData, setFormData] = useState({
        course_id: '',
        file: '',
    });

    const [errorMessages, setErrorMessages] = useState({
        course_id: '',
        file: '',
    });
    const [disabledBtn, setBtnDisable] = useState(false);
    const [fileContent, setFileContent] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState({
        id: '',
        name: ''
    });
    const [isErrorMessage, setErrorMessage] = useState(false);

    const fetchAllCourses = useCallback(() => {
        dispatch(getAllActiveCourses());
    }, [dispatch]);

    useEffect(() => {
        fetchAllCourses();
    }, [fetchAllCourses]);

    const handleSelect = (option) => {
        setFormData({ ...formData, course_id: option.id });
        setErrorMessages({ ...errorMessages, course_id: "" });
        setSelectedCourse({
            id: option.id,
            name: option.name
        });
    };

    const expectedHeaders = ["student_name", "completion_date", "certificate_date", "batch"];

    const validateHeaders = (headers) => {
        const details = expectedHeaders.every(header => headers.includes(header));
        return details
    };

    const readFileContent = async (file) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const data = event.target.result;
            let headers;
            const workbook = XLSX.read(data, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });
            headers = worksheet[0];
            if (validateHeaders(headers)) {
                setErrorMessage(false);
                const headerMapping = {};
                headers.forEach((header, index) => {
                    headerMapping[header] = index;
                });
                const formattedContent = worksheet.slice(1).map(row => {
                    if (row[headerMapping['birthday']] && !isNaN(row[headerMapping['birthday']])) {
                        row[headerMapping['birthday']] = XLSX.SSF.format("yyyy-mm-dd", row[headerMapping['birthday']]); // Convert to date string
                    }
                    return row;
                });

                setFileContent([headers, ...formattedContent]);
            } else {
                setSelectedFile(null);
                setErrorMessage("Invalid file headers. Expected headers: " + expectedHeaders.join(", "));
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleFileChange = (event) => {
        setErrorMessages({ ...errorMessages, file: '' });
        const file = event.target.files[0];
        setSelectedFile(file);
        readFileContent(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const droppedFile = files[0];
            setSelectedFile(droppedFile);
            setErrorMessage("");
            readFileContent(droppedFile);
        }
        e.currentTarget.classList.remove('highlight');
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };

    const handleFileUpload = async () => {
        if (!selectedFile || selectedCourse.name === '') {
            if (!selectedFile) {
                setErrorMessages({ ...errorMessages, file: "Please select a file to upload." });
            }
            if (selectedCourse.name === '') {
                setErrorMessages({ ...errorMessages, course_id: "Course is required." });
            }
            return;
        }
        const headers = fileContent[0];
        const rows = fileContent.slice(1);

        const headerMapping = {};
        headers.forEach((header, index) => {
            headerMapping[header] = index;
        });

        const formattedRows = rows.map((row) => {
            return row;
        });

        const newSheetData = [headers, ...formattedRows];
        const newWorksheet = XLSX.utils.aoa_to_sheet(newSheetData);  // Convert array to worksheet
        const newWorkbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, "Sheet1");  // Append the new sheet to the workbook

        const workbookBinary = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([workbookBinary], { type: "application/octet-stream" });

        const formData = new FormData();
        formData.append('file', blob);
        formData.append('course_id', selectedCourse.id)
        try {
            const response = await dispatch(addNewBulk(formData)).unwrap();
            showToast('success', response.message);
            setBtnDisable(false);
            setSelectedFile(null);
            setFileContent(null);

        } catch (error) {
            showToast('error', error.message);
            setSelectedFile(null);
            setFileContent(null);
        }
    };

    const downloadEmptyExcel = () => {
        const headers = [["student_name", "completion_date", "certificate_date", "batch"]];

        const worksheet = XLSX.utils.aoa_to_sheet(headers);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

        XLSX.writeFile(workbook, "IBSC Sample File.xlsx");
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Bulk
                </h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Course<span className={`required-star`}>*</span>
                                    </label>
                                    <Dropdown
                                        ref={courseDropdownRef}
                                        options={activeCourses}
                                        placeholder="Choose an Option"
                                        onSelect={handleSelect}
                                        value={selectedCourse.name}
                                    />
                                    {errorMessages.course_id && (
                                        <p className={`text-danger`}>{errorMessages.course_id}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        File<span className={`required-star`}>*</span>
                                    </label>
                                    <div
                                        onDrop={handleDrop}
                                        onDragOver={(event) => event.preventDefault()}
                                        onClick={handleClick}
                                        className={`drop-input`}
                                    >
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept=".xlsx, .xls"
                                            onChange={handleFileChange}
                                            style={{ display: "none" }}
                                        />
                                        <p>Drag & Drop a file here or click to select</p>
                                    </div>
                                    {errorMessages.file && (
                                        <p className={`text-danger`}>{errorMessages.file}</p>
                                    )}
                                    {isErrorMessage && <p className={`text-danger`}>{isErrorMessage}</p>}
                                </div>
                            </div>
                        </div>
                        <div className={`table-container`}>
                            {fileContent &&
                                <table className={styles.table}>
                                    <thead className={styles.thead}>
                                        <tr>
                                            <th className={styles.theadD}>Document Ref No</th>
                                            <th className={styles.theadD}>Student Print Name</th>
                                            <th className={styles.theadD}>Completion Date</th>
                                            <th className={styles.theadD}>Cerificate Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className={styles.tbody}>
                                        {fileContent?.slice(1, 6).map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {row.map((cell, cellIndex) => (
                                                    <td className={styles.tableCell} key={cellIndex}>{cell}</td>
                                                ))}
                                            </tr>
                                        ))}
                                        {fileContent?.length > 6 && (
                                            <tr>
                                                <td colSpan="5">... and {fileContent?.length - 6} more rows</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            }
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={handleFileUpload}
                        >
                            {isBulkCreating ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                            <button
                                className={`btn-primary ${styles.downloadBtn}`}
                                type='button'
                                onClick={downloadEmptyExcel}
                            >
                                Download Sample <FontAwesomeIcon icon={faDownload} />
                            </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BulkUpload;
